<template>
  <section class="body">
    <div>
      <a-form ref="formRefCall" :model="formState" layout="vertical">
        <div class="modal-top-section modal-top-section--align-center">
          <h3 class="modal-top-section__title">Configuración para llamadas entrantes e IVR</h3>

          <span class="modal-top-section__text">
            Las siguientes configuraciones se habilitaran cuando un contacto llame directamente a un
            número entrante o sea transferido desde un IVR.
          </span>
        </div>
        <div class="section--agents">
          <p class="modal-top-section__subtitle-black">Audio de espera</p>
          <p class="modal-top-section__text">
            Audio que escuchará el contacto repetidamente hasta ser transferido a un usuario.
          </p>
          <ArchivePicker
            v-model:value="formState.waiting_file_audio"
            :supportedTypes="['.mp3']"
            :maxMBSize="3"
          />
          <AudioPreview
            :node-src="isEdit ? record?.waiting_file_audio_url : ''"
            :file="formState.waiting_file_audio"
          />
        </div>

        <div class="section--agents" style="margin-top: 24px">
          <div v-if="portfolioSelected?.enable_queues_priority">
            <p class="modal-top-section__subtitle-black">
              Prioridad de cola
            </p>
            <p class="modal-top-section__text">
              Ingresa un número de prioridad para la cola al recibir llamadas. 
            </p>
            <a-form-item name="priority">
              <template #label>
                <div>
                  Número de prioridad
                  <a-tooltip>
                    <template #title>
                      <p>
                        Si el número de prioridad es <br/>
                        igual o cercano a 1, la cola será <br/>
                        más prioritaria.
                      </p>
                    </template>
                    <InfoCircleOutlined style="margin: 0 5px" />                  
                  </a-tooltip>
                  <span class="text-info">(opcional)</span>
                </div>
              </template>
                <a-input-number
                v-model:value="formState.priority"
                :min="1"
                :max="100"
                :placeholder="'Ingresar número'"
                :step="1"
                :precision="0"
                :style="{ width: '100%' }"
                @blur="formState.priority = formState.priority || 1"
              />
              <div class="text-entity">
                Valor mínimo desde 1.
              </div>
            </a-form-item>
          </div>
          <a-divider/>
          <div>
            <p class="modal-top-section__subtitle-black">
              Entrega de llamadas a usuarios por prioridad
              <a-switch v-model:checked="formState.enable_users_priority" size="small" />
            </p>
            <!-- <a-checkbox v-model:value="record?.priority">
              Activar prioridad de cola
            </a-checkbox> -->
            <p class="modal-top-section__text">
              Define el orden de entrega de llamadas a los usuarios según su número de prioridad. <strong>Recuerda que, al activar esta opción, la prioridad se configura al agregar los usuarios.</strong>
            </p>
          </div>
          
          <a-divider/>
          <p class="modal-top-section__subtitle-black">
            Envío de plantillas de WhatsApp para llamadas perdidas
          </p>
          <p class="modal-top-section__text">
            Envía mensajes automáticos cuando un contacto cuelga la llamada mientras espera ser
            atendido por un agente de esta cola. (Opcional)
          </p>
          <a-flex :justify="'space-between'" :align="'center'" :style="{ width: '100%' }">
            <div>
              <p class="subtitle-agents">Plantilla de Whatsapp</p>
            </div>
            <div>
              <a-popover placement="right" trigger="click" overlayClassName="whatsapp-popover" @visibleChange="incrementScroll">
                <template #content v-if="formState.template != null">
                  <div class="custom-popover-content">
                    <WhatsappTemplatePhone
                      v-if="currentTemplate"
                      class="template-preview"
                      :content="currentTemplate.template"
                      :templateType="WhatsappTemplateTypes.Text"
                      :type="'popover'"
                      :triggerScroll="triggerScroll"
                    />
                  </div>
                </template>
                <a-button type="link" block :disabled="formState.template == null">
                  Previsualiza tu plantilla
                </a-button>
              </a-popover>
            </div>
          </a-flex>
          <a-form-item name="plantilla">
            <a-select
              :getPopupContainer="(triggerNode) => triggerNode.parentNode"
              v-model:value="formState.template"
              :disabled="templates.length < 2"
              placeholder="Seleccionar una plantilla"
              @change="handleChangeTemplate"
            >
              <a-select-option v-for="template in templates" :key="template.id">
                {{ template.name }}
              </a-select-option>
            </a-select>
            <div class="text-entity">
              <p v-if="idQueue == ''">
                Esta cola es nueva y no tiene plantillas asignadas. Puedes asignarlas desde el
                editor de plantillas en <a>Mis canales</a>.
              </p>
              <p v-else-if="templates.length < 2">
                Esta cola no tiene plantillas asignadas. Puedes asignarlas desde el editor de
                plantillas en <a @click="handleRedirectToChannels">Mis canales</a>.
              </p>
              <p v-else>
                El identificador <span class="text--bold">Waba Staging</span> está destinado
                únicamente para el envío de esta plantilla, pero no se encuentra asociado a esta
                cola para la recepción de nuevos mensajes.
              </p>
            </div>
          </a-form-item>
        </div>
      </a-form>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted, computed, watch, inject } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";

import { WppTemplate } from "@/@types/global";
import { WhatsappTemplateTypes } from "@/app/channels/utils/enums";
import { fetchTemplates } from "@/app/workspace/services";

import WhatsappTemplatePhone from "@/app/shared/components/organisms/WhatsappTemplatePhone.vue";
import ArchivePicker from "@/app/shared/components/molecules/ArchivePicker.vue";
import AudioPreview from "@/app/ivr/bot/components/buttons/AudioPreview.vue";
import { InfoCircleOutlined } from "@ant-design/icons-vue";

const store = useStore();
const formRefCall = ref();
const router = useRouter();

const props = defineProps({
  idQueue: {
    type: String,
    default: () => "",
  },
  isEdit: {
    type: Boolean,
    default: false,
  },
});

const emits = defineEmits(["closeDrawer", "updateAudio"]);

const idQueue = ref(props.idQueue);
const enableUsersPriority = inject<any>("enableUsersPriority");

const templates = ref<WppTemplate[]>([]);
const record = computed(() => store.getters["getQueueState"]);

const formState = reactive({
  template: record.value.whatsapp_template,
  waiting_file_audio: record.value.waiting_file_audio,
  priority: record.value.priority || 1,
  enable_users_priority: record.value.enable_users_priority,
});
// Valor solo para mover el scroll hacia arriba
const triggerScroll = ref(0);

const incrementScroll = () => triggerScroll.value++;

watch(
  () => formState.enable_users_priority,
  (value) => {
    enableUsersPriority.value = value;
  },
  { immediate: true }
)
const portfolioSelected = computed(() => 
  store.getters["getFilteredPortfolios"].find((el: any) => 
    el.id == store.getters["getQueueState"].portfolios[0]
  )
);
const currentTemplate = ref<WppTemplate>();

const handleChangeTemplate = (newValue: number) => {
  if (!newValue) return;
  currentTemplate.value = templates.value.find((template) => template.id === newValue);
};

const handleOk = async (current: number) => {
  let value = current;
  value = current + 1;
  store.dispatch("setQueueProperties", {
    whatsapp_template: formState.template,
    waiting_file_audio: formState.waiting_file_audio || null,
    priority: formState.priority,
    enable_users_priority: formState.enable_users_priority,
  });
  return value;
};

const handleRedirectToChannels = () => {
  router.push(`/channels/board`);
};

watch(
  () => formState.waiting_file_audio,
  () => {
    emits("updateAudio");
  }
);

onMounted(async () => {
  templates.value = [
    { id: null, name: "No usar ninguna plantilla" },
    ...(await fetchTemplates(idQueue.value, "", true)),
  ];
  currentTemplate.value = templates.value.find((template) => template.id === formState.template);
});

defineExpose({ handleOk });
</script>

<style lang="sass" scoped>
.body
  padding: 0 60px

.text-info
  color: $gray-7
  font-size: 14px

.text-entity
  display: flex
  color: $gray-7
  font-size: 14px
  margin-top: 2px
  margin-bottom: -10px

  p
    display: inline

.section--agents
  margin-top: 25px

.padding-10
  padding: 0 50px
  justify-content: space-between

.dropdown-default__wrapper
  margin: 36px auto
  width: 190px
  text-align: center
  color: $gray-8

.title-agents
  margin-bottom: 0px
  font-weight: 600
  color: $gray-9
  size: 16px
  line-height: 24px

.info-agents
  font-weight: 400
  color: $gray-8
  size: 14px
  line-height: 22px

.subtitle-agents
  margin-bottom: 0px
  font-weight: 500
  size: 14px
  line-height: 22px

.modal-top-section__subtitle-black
  margin-bottom: 2px
  display: flex
  align-items: center
  gap: 5px
.text--bold
  font-weight: 600

.template-preview
  width: 70%
  margin: 0 auto
  margin-bottom: 30px

.custom-popover-content
  background-color: transparent

.ant-popover-inner img
  max-width: 100%
  height: auto
  display: block
</style>
